import { useRef, useState, useEffect, useCallback } from 'react';

export interface UseSwipeToDismissOptions {
  /** Width of the action panel in pixels (default: 140) */
  actionPanelWidth?: number;
  /** Minimum horizontal distance to trigger reveal (default: 60) */
  threshold?: number;
  /** Whether swipe is enabled (gates on mobile viewport) */
  enabled: boolean;
  /** Callback when another card starts swiping — used for coordination */
  onSwipeStart?: () => void;
  /** External signal to close this card */
  forceClose?: boolean;
}

export interface UseSwipeToDismissReturn {
  /** Ref to attach to the swipeable container element */
  containerRef: React.RefObject<HTMLDivElement>;
  /** Current horizontal offset in pixels (0 = closed, panelWidth = open) */
  offset: number;
  /** Whether the card is in the "open" (action panel revealed) state */
  isOpen: boolean;
  /** Whether a swipe gesture is actively in progress */
  isSwiping: boolean;
  /** Programmatically close the panel */
  close: () => void;
}

type SwipeState = 'idle' | 'tracking' | 'open' | 'closed';

export function useSwipeToDismiss({
  actionPanelWidth = 140,
  threshold = 60,
  enabled,
  onSwipeStart,
  forceClose,
}: UseSwipeToDismissOptions): UseSwipeToDismissReturn {
  const containerRef = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);

  // Use refs for touch tracking to avoid re-renders during gesture
  const stateRef = useRef<SwipeState>('idle');
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const directionDecidedRef = useRef(false);

  const isOpen = offset === actionPanelWidth;

  const close = useCallback(() => {
    stateRef.current = 'closed';
    setOffset(0);
    setIsSwiping(false);
  }, []);

  // React to forceClose prop changes
  useEffect(() => {
    if (forceClose && offset !== 0) {
      close();
    }
  }, [forceClose, close, offset]);

  // Attach/detach touch listeners
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!enabled) return;

    const container = containerRef.current;
    if (!container) return;

    function handleTouchStart(e: TouchEvent) {
      if (!containerRef.current) return;

      const touch = e.touches[0];
      startXRef.current = touch.clientX;
      startYRef.current = touch.clientY;
      directionDecidedRef.current = false;
      stateRef.current = 'tracking';
      setIsSwiping(true);

      if (onSwipeStart) {
        onSwipeStart();
      }
    }

    function handleTouchMove(e: TouchEvent) {
      if (!containerRef.current) return;
      if (stateRef.current !== 'tracking') return;

      const touch = e.touches[0];
      const deltaX = startXRef.current - touch.clientX; // positive = left swipe
      const deltaY = Math.abs(touch.clientY - startYRef.current);

      // Decide direction early: if vertical > horizontal, cancel swipe
      if (!directionDecidedRef.current) {
        const absDeltaX = Math.abs(deltaX);
        // Wait until there's enough movement to decide direction
        if (absDeltaX < 5 && deltaY < 5) return;

        directionDecidedRef.current = true;

        if (deltaY > absDeltaX) {
          // Vertical movement dominates — cancel swipe, allow scrolling
          stateRef.current = 'idle';
          setIsSwiping(false);
          setOffset(0);
          return;
        }
      }

      // Prevent vertical scrolling while swiping horizontally
      e.preventDefault();

      // Clamp offset: 0 to actionPanelWidth
      const clampedOffset = Math.max(0, Math.min(deltaX, actionPanelWidth));
      setOffset(clampedOffset);
    }

    function handleTouchEnd() {
      if (!containerRef.current) return;
      if (stateRef.current !== 'tracking') {
        setIsSwiping(false);
        return;
      }

      setIsSwiping(false);

      // Read current offset via a synchronous check
      setOffset((currentOffset) => {
        if (currentOffset > threshold) {
          // Snap open
          stateRef.current = 'open';
          return actionPanelWidth;
        } else {
          // Snap closed
          stateRef.current = 'closed';
          return 0;
        }
      });
    }

    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [enabled, actionPanelWidth, threshold, onSwipeStart]);

  return {
    containerRef,
    offset,
    isOpen,
    isSwiping,
    close,
  };
}
